import { Link } from 'react-router-dom';
import { useState } from 'react';

const plans = [
  {
    name: 'Free',
    price: 0,
    description: 'Perfect for hobby projects and testing',
    features: [
      { text: '3 Static Sites', included: true },
      { text: '5 GB Storage', included: true },
      { text: 'Free SSL Certificates', included: true },
      { text: 'SSH/SFTP Access', included: true },
      { text: 'Basic Analytics', included: true },
      { text: 'Community Support', included: true },
      { text: 'AI Assistant Deploy', included: false },
      { text: 'Application Containers', included: false },
      { text: 'Databases', included: false },
    ],
    cta: 'Get Started Free',
    featured: false,
  },
  {
    name: 'Pro',
    price: 15,
    description: 'For professional developers and growing projects',
    features: [
      { text: 'Everything in Free', included: true },
      { text: '10 Static Sites', included: true },
      { text: '25 GB Storage', included: true },
      { text: '3 Application Containers', included: true },
      { text: '2 Databases (PostgreSQL, MySQL, Redis)', included: true },
      { text: '3 Shell/Desktop Machines', included: true },
      { text: 'AI Assistant Deploy', included: true },
      { text: 'API Keys & Automation', included: true },
      { text: 'Custom Domains', included: true },
      { text: 'Advanced Analytics', included: true },
      { text: 'Priority Email Support', included: true },
    ],
    cta: 'Start Pro Trial',
    featured: true,
  },
];

const faqs = [
  {
    question: 'What does "AI Assistant Integration" mean?',
    answer: 'Your AI coding assistant (like Claude, ChatGPT, or GitHub Copilot) can deploy directly to your account using API keys. No manual steps needed - just ask your AI to deploy and it handles everything.',
  },
  {
    question: 'Can I upgrade or downgrade my plan anytime?',
    answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate your billing accordingly.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, MasterCard, American Express, Discover) and PayPal.',
  },
  {
    question: 'Is there a free trial for paid plans?',
    answer: 'Yes! Pro plan comes with a 14-day free trial. No credit card required to start. You can explore all features before committing.',
  },
  {
    question: 'What happens if I exceed my resource limits?',
    answer: 'We\'ll notify you when you\'re approaching your limits. You can upgrade your plan anytime to get more resources.',
  },
  {
    question: 'Can I use my own domain?',
    answer: 'Yes! Pro plan supports custom domains with free SSL certificates. Just point your DNS to our servers and we handle the rest.',
  },
  {
    question: 'Do you offer refunds?',
    answer: 'Yes, we offer a 30-day money-back guarantee on all paid plans. If you\'re not satisfied, contact us for a full refund.',
  },
];

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/5 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Simple,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              Transparent
            </span>{' '}
            Pricing
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-8">
            Start free. Scale as you grow. AI-powered deployment at every level.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 p-1 bg-zinc-800 rounded-full">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Yearly
              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative p-8 rounded-2xl ${
                  plan.featured
                    ? 'bg-gradient-to-b from-orange-500/10 to-transparent border-2 border-orange-500/50 shadow-xl shadow-orange-500/10'
                    : 'bg-zinc-900/50 border border-zinc-800'
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-full text-sm font-semibold text-white">
                    Most Popular
                  </div>
                )}

                <h3 className={`text-lg font-semibold mb-2 ${plan.featured ? 'text-orange-400' : 'text-zinc-400'}`}>
                  {plan.name}
                </h3>

                <div className="flex items-baseline gap-1 mb-2">
                  {plan.price !== null ? (
                    <>
                      <span className="text-4xl font-bold text-white">
                        ${billingCycle === 'yearly' ? Math.round(plan.price * 0.8) : plan.price}
                      </span>
                      <span className="text-zinc-500">/mo</span>
                    </>
                  ) : (
                    <span className="text-4xl font-bold text-white">Custom</span>
                  )}
                </div>

                {billingCycle === 'yearly' && plan.price && plan.price > 0 && (
                  <p className="text-sm text-green-400 mb-4">
                    ${Math.round(plan.price * 0.8 * 12)}/year
                  </p>
                )}

                <p className="text-sm text-zinc-400 mb-6 min-h-[3rem]">{plan.description}</p>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature.text} className="flex items-start gap-2 text-sm">
                      <span
                        className={`material-icons text-lg flex-shrink-0 ${
                          feature.included
                            ? (feature as any).highlight
                              ? 'text-orange-400'
                              : 'text-green-400'
                            : 'text-zinc-700'
                        }`}
                      >
                        {feature.included ? 'check_circle' : 'cancel'}
                      </span>
                      <span className={`${feature.included ? 'text-zinc-300' : 'text-zinc-600'} ${(feature as any).highlight ? 'font-medium text-orange-300' : ''}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://dashboard.wiserwebcloud.com/signup"
                  className={`block w-full py-3 text-center font-semibold rounded-lg transition-all ${
                    plan.featured
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:shadow-glow'
                      : 'bg-zinc-800 text-white border border-zinc-700 hover:bg-zinc-700'
                  }`}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>

          {/* Plan Note */}
          <div className="mt-16 text-center">
            <p className="text-sm text-zinc-500">All plans include unlimited bandwidth and 99.9% uptime SLA</p>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-24 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Compare Plans
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full border border-zinc-800 rounded-xl overflow-hidden">
              <thead className="bg-zinc-900">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-zinc-400">Free</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-orange-400 bg-orange-500/5">Pro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {[
                  { feature: 'Static Sites', values: ['3', '10'] },
                  { feature: 'Storage', values: ['5 GB', '25 GB'] },
                  { feature: 'App Containers', values: ['—', '3'] },
                  { feature: 'Databases', values: ['—', '2'] },
                  { feature: 'Shell/Desktop Machines', values: ['—', '3'] },
                  { feature: 'AI Assistant Deploy', values: ['—', '✓'] },
                  { feature: 'API Keys & Automation', values: ['—', '✓'] },
                  { feature: 'Custom Domains', values: ['—', '✓'] },
                  { feature: 'Advanced Analytics', values: ['—', '✓'] },
                  { feature: 'Support', values: ['Community', 'Priority Email'] },
                ].map((row) => (
                  <tr key={row.feature} className="hover:bg-zinc-900/50">
                    <td className="px-6 py-4 text-sm text-white font-medium">{row.feature}</td>
                    {row.values.map((value, idx) => (
                      <td
                        key={idx}
                        className={`px-6 py-4 text-sm text-center ${
                          idx === 1 ? 'bg-orange-500/5 text-orange-400 font-medium' : 'text-zinc-400'
                        }`}
                      >
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <span className="font-medium text-white">{faq.question}</span>
                  <span className={`material-icons text-zinc-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}>
                    expand_more
                  </span>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-zinc-400 border-t border-zinc-800 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-b from-zinc-900/50 to-base">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-full text-sm text-orange-400 mb-6">
              <span className="material-icons text-sm">smart_toy</span>
              AI-Powered Deployment
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Deploy with AI?
          </h2>
          <p className="text-lg text-zinc-400 mb-8">
            Start your 14-day free trial. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://dashboard.wiserwebcloud.com/signup"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-xl hover:shadow-glow transition-all"
            >
              <span className="material-icons">rocket_launch</span>
              Start Free Trial
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold text-white bg-zinc-800 border border-zinc-700 rounded-xl hover:bg-zinc-700 transition-colors"
            >
              <span className="material-icons">mail</span>
              Questions? Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
